import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Radio, FileText, ChevronRight, CheckCheck } from "lucide-react";
import api from "@/lib/api";

export default function NotificationBell() {
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchCount();
    const interval = setInterval(fetchCount, 15000);
    return () => clearInterval(interval);
  }, []);

  const fetchCount = async () => {
    try {
      const { data } = await api.get("/my-notifications/unread-count");
      setCount(data.count || 0);
    } catch (err) { console.error(err); }
  };

  const toggleOpen = async () => {
    if (!open) {
      setLoading(true);
      try {
        const { data } = await api.get("/my-notifications");
        setNotifications(data);
      } catch (err) { console.error(err); }
      setLoading(false);
    }
    setOpen(!open);
  };

  const markAllRead = async () => {
    try {
      await api.post("/my-notifications/read-all");
      setCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (err) { console.error(err); }
  };

  const handleNotifClick = (notif) => {
    if (notif.course_id) {
      if (notif.notif_type === "live") {
        navigate(`/courses/${notif.course_id}`);
      } else if (notif.notif_type === "notes") {
        navigate(`/watch/${notif.course_id}`);
      } else {
        navigate(`/courses/${notif.course_id}`);
      }
    }
    setOpen(false);
  };

  const ICONS = {
    live: { icon: Radio, color: "text-red-500", bg: "bg-red-50" },
    notes: { icon: FileText, color: "text-green-500", bg: "bg-green-50" },
  };

  const timeAgo = (dateStr) => {
    if (!dateStr) return "";
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  return (
    <div className="relative" data-testid="notification-bell">
      <button
        data-testid="notification-bell-btn"
        onClick={toggleOpen}
        className="p-2 relative transition-none"
      >
        <Bell size={20} />
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="notif-badge">
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-12 w-80 bg-white shadow-xl z-50 max-h-[70vh] flex flex-col border border-slate-200" data-testid="notification-dropdown">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
              <h3 className="text-sm font-bold text-slate-900">Notifications</h3>
              {count > 0 && (
                <button
                  data-testid="mark-all-read-btn"
                  onClick={markAllRead}
                  className="text-[10px] font-bold text-[#0284C7] flex items-center gap-1 transition-none"
                >
                  <CheckCheck size={12} /> Mark all read
                </button>
              )}
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="p-6 text-center text-slate-400 text-xs">Loading...</div>
              ) : notifications.length === 0 ? (
                <div className="p-8 text-center">
                  <Bell size={32} className="mx-auto text-slate-200 mb-2" />
                  <p className="text-xs text-slate-400">No notifications yet</p>
                </div>
              ) : (
                notifications.map((notif) => {
                  const config = ICONS[notif.notif_type] || { icon: Bell, color: "text-[#0284C7]", bg: "bg-blue-50" };
                  const Icon = config.icon;
                  return (
                    <button
                      key={notif.id}
                      data-testid={`notif-item-${notif.id}`}
                      onClick={() => handleNotifClick(notif)}
                      className={`w-full flex items-start gap-3 px-4 py-3 text-left border-b border-slate-50 transition-none ${
                        !notif.read ? "bg-blue-50/50" : ""
                      }`}
                    >
                      <div className={`w-9 h-9 flex items-center justify-center flex-shrink-0 mt-0.5 ${config.bg}`}>
                        <Icon size={16} className={config.color} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className={`text-xs font-bold ${!notif.read ? "text-slate-900" : "text-slate-600"}`}>
                            {notif.title}
                          </p>
                          {!notif.read && <span className="w-2 h-2 bg-[#0284C7] flex-shrink-0" />}
                        </div>
                        <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">{notif.message}</p>
                        <p className="text-[10px] text-slate-400 mt-1">{timeAgo(notif.created_at)}</p>
                      </div>
                      <ChevronRight size={14} className="text-slate-300 mt-1 flex-shrink-0" />
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
